import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../api";
import { useLang } from "../i18n";
import { toast } from "sonner";
import { Trash, PencilSimple, Plus, SignOut } from "@phosphor-icons/react";

const emptyProperty = {
  title_fr: "", title_en: "",
  neighborhood: "", city: "Montréal", price: 0,
  bedrooms: 0, bathrooms: 0, living_area_sqft: 0,
  property_type: "condo", status: "for_sale", featured: false,
  description_fr: "", description_en: "", images: [],
};

export default function AdminDashboard() {
  const { t } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null);
  const email = localStorage.getItem("ra_admin_email");

  useEffect(() => {
    if (!localStorage.getItem("ra_admin_token")) {
      nav("/admin", { replace: true });
      return;
    }
    loadAll();
  }, [nav]);

  const loadAll = async () => {
    try {
      const [l, p] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/properties", { params: { limit: 100 } }),
      ]);
      setLeads(l.data);
      setProperties(p.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const logout = () => {
    localStorage.removeItem("ra_admin_token");
    localStorage.removeItem("ra_admin_email");
    nav("/admin", { replace: true });
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (e) { toast.error("Update failed"); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) { toast.error("Delete failed"); }
  };

  const saveProperty = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editing,
        price: parseInt(editing.price, 10) || 0,
        bedrooms: parseInt(editing.bedrooms, 10) || 0,
        bathrooms: parseInt(editing.bathrooms, 10) || 0,
        living_area_sqft: parseInt(editing.living_area_sqft, 10) || 0,
        images: typeof editing.images === "string"
          ? editing.images.split(",").map((s) => s.trim()).filter(Boolean)
          : editing.images,
      };
      if (editing.id) {
        await api.put(`/admin/properties/${editing.id}`, payload);
      } else {
        await api.post("/admin/properties", payload);
      }
      setEditing(null);
      loadAll();
      toast.success("Saved");
    } catch (err) { toast.error("Save failed"); }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/admin/properties/${id}`);
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-[#F5F5F0]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between sticky top-0 bg-[#0F0F0F]/90 backdrop-blur-xl z-10">
        <Link to="/" className="flex items-center gap-3">
          <span className="font-serif text-2xl text-[#D4AF37]">Roland Azar</span>
          <span className="text-[0.65rem] uppercase tracking-[0.25em] text-white/40 hidden md:inline">Admin</span>
        </Link>
        <div className="flex items-center gap-5">
          <span className="text-xs text-white/50 hidden md:inline">{email}</span>
          <button onClick={logout} className="text-[0.7rem] uppercase tracking-[0.2em] text-white/60 hover:text-[#D4AF37] flex items-center gap-2" data-testid="admin-logout">
            <SignOut size={16} weight="thin" /> {t.admin.logout}
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 mb-10 border-b border-white/10">
          {["leads", "properties"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-6 py-4 text-[0.72rem] uppercase tracking-[0.25em] transition-colors border-b-2 ${
                tab === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50 hover:text-white"
              }`}
              data-testid={`tab-${k}`}
            >
              {t.admin[k]} ({k === "leads" ? leads.length : properties.length})
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="space-y-3" data-testid="leads-list">
            {leads.length === 0 && <p className="text-white/40 py-12 text-center">—</p>}
            {leads.map((l) => (
              <div key={l.id} className="bg-[#141414] border border-white/5 p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                <div className="md:col-span-3">
                  <div className="font-serif text-xl text-[#F5F5F0]">{l.name}</div>
                  <div className="text-xs text-white/50 mt-1">{new Date(l.created_at).toLocaleString()}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[#D4AF37] mt-2">{l.source}</div>
                </div>
                <div className="md:col-span-4 text-sm space-y-1 text-white/70">
                  <div><a href={`mailto:${l.email}`} className="hover:text-[#D4AF37]">{l.email}</a></div>
                  <div>{l.phone || "—"}</div>
                </div>
                <div className="md:col-span-3 text-sm text-white/60">{l.message || "—"}</div>
                <div className="md:col-span-2 flex flex-col gap-2 items-end">
                  <select
                    value={l.status}
                    onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                    className="bg-[#0F0F0F] border border-white/10 text-xs px-3 py-2 uppercase tracking-wider"
                    data-testid={`lead-status-${l.id}`}
                  >
                    <option value="new">{t.admin.status_new}</option>
                    <option value="contacted">{t.admin.status_contacted}</option>
                    <option value="archived">{t.admin.status_archived}</option>
                  </select>
                  <button onClick={() => deleteLead(l.id)} className="text-white/40 hover:text-[#D4AF37] text-xs flex items-center gap-1" data-testid={`lead-delete-${l.id}`}>
                    <Trash size={14} weight="thin" /> {t.admin.delete}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "properties" && (
          <div>
            <div className="flex justify-end mb-6">
              <button onClick={() => setEditing({ ...emptyProperty })} className="btn-gold flex items-center gap-2" data-testid="add-property-btn">
                <Plus size={14} weight="bold" /> {t.admin.add_property}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="properties-list">
              {properties.map((p) => (
                <div key={p.id} className="bg-[#141414] border border-white/5 p-5 flex gap-4">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-24 h-24 object-cover" />}
                  <div className="flex-1">
                    <div className="font-serif text-lg text-[#F5F5F0]">{p.title_fr}</div>
                    <div className="text-xs text-white/50">{p.neighborhood} · {p.property_type}</div>
                    <div className="text-sm text-[#D4AF37] mt-1">${p.price.toLocaleString()}</div>
                    <div className="flex gap-2 mt-2">
                      <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 bg-white/5 text-white/60">{p.status}</span>
                      {p.featured && <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37]">Featured</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => setEditing({ ...p, images: (p.images || []).join(", ") })} className="text-white/50 hover:text-[#D4AF37]" data-testid={`edit-prop-${p.id}`}>
                      <PencilSimple size={16} weight="thin" />
                    </button>
                    <button onClick={() => deleteProperty(p.id)} className="text-white/50 hover:text-[#D4AF37]" data-testid={`del-prop-${p.id}`}>
                      <Trash size={16} weight="thin" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="property-editor">
          <form onSubmit={saveProperty} className="bg-[#141414] border border-[#D4AF37]/30 w-full max-w-3xl p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-[#F5F5F0] mb-6">{editing.id ? t.admin.edit : t.admin.add_property}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input required placeholder="Titre FR" className="input-line" value={editing.title_fr} onChange={(e) => setEditing({ ...editing, title_fr: e.target.value })} />
              <input required placeholder="Title EN" className="input-line" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} />
              <input required placeholder="Quartier" className="input-line" value={editing.neighborhood} onChange={(e) => setEditing({ ...editing, neighborhood: e.target.value })} />
              <input required placeholder="Ville" className="input-line" value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
              <input required type="number" placeholder="Prix (CAD)" className="input-line" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              <input type="number" placeholder="Chambres" className="input-line" value={editing.bedrooms} onChange={(e) => setEditing({ ...editing, bedrooms: e.target.value })} />
              <input type="number" placeholder="SDB" className="input-line" value={editing.bathrooms} onChange={(e) => setEditing({ ...editing, bathrooms: e.target.value })} />
              <input type="number" placeholder="pi²" className="input-line" value={editing.living_area_sqft} onChange={(e) => setEditing({ ...editing, living_area_sqft: e.target.value })} />
              <select className="input-line" value={editing.property_type} onChange={(e) => setEditing({ ...editing, property_type: e.target.value })}>
                <option value="condo">Condo</option>
                <option value="duplex">Duplex</option>
                <option value="house">House</option>
                <option value="penthouse">Penthouse</option>
              </select>
              <select className="input-line" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="for_sale">For sale</option>
                <option value="sold">Sold</option>
                <option value="off_market">Off-market</option>
              </select>
              <label className="flex items-center gap-3 text-sm text-white/70 col-span-2">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                Featured on home page
              </label>
              <textarea rows={3} placeholder="Description FR" className="input-line col-span-2 resize-none" value={editing.description_fr} onChange={(e) => setEditing({ ...editing, description_fr: e.target.value })} />
              <textarea rows={3} placeholder="Description EN" className="input-line col-span-2 resize-none" value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} />
              <input placeholder="Images (comma-separated URLs)" className="input-line col-span-2" value={editing.images} onChange={(e) => setEditing({ ...editing, images: e.target.value })} />
            </div>
            <div className="flex justify-end gap-4 mt-8">
              <button type="button" onClick={() => setEditing(null)} className="btn-outline" data-testid="editor-cancel">{t.admin.cancel}</button>
              <button type="submit" className="btn-gold" data-testid="editor-save">{t.admin.save}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
